import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FALLBACK_CONTENT = `## 1. Acceptance of Terms

By accessing or using Thrive, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.

## 2. Description of Service

Thrive is a beekeeping management platform that allows users to track hives, record inspections, manage apiaries, and access beekeeping resources. We provide both free and paid subscription tiers with varying features and limits.

## 3. User Accounts

You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must provide accurate and complete information when creating an account.

## 4. User Content

You retain ownership of any content you submit to Thrive, including inspection records, notes, and photos. By submitting content, you grant us a license to store, display, and process this content to provide our services.

## 5. Acceptable Use

You agree not to misuse our services, including but not limited to: attempting to gain unauthorized access, interfering with other users, or using the service for illegal purposes.

## 6. Subscriptions and Payments

Paid subscriptions are billed in advance on a monthly or annual basis. You may cancel your subscription at any time, but refunds are subject to our refund policy. We reserve the right to change subscription prices with notice.

## 7. Limitation of Liability

Thrive is provided "as is" without warranties of any kind. We are not liable for any damages arising from the use of our service, including loss of data or business interruption.

## 8. Termination

We may suspend or terminate your account if you violate these terms. Upon termination, your right to use the service ceases immediately.

## 9. Changes to Terms

We may update these Terms of Service from time to time. We will notify users of significant changes via the application. Continued use of the service constitutes acceptance of the updated terms.

## 10. Contact

If you have questions about these Terms of Service, please contact us at macmanclan@gmail.com`;

const TermsOfService = () => {
  const [appSettings, setAppSettings] = useState({ app_name: 'Thrive' });
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/app-settings/public`)
      .then(res => res.json())
      .then(data => data.app_name && setAppSettings(data))
      .catch(() => {});

    fetch(`${API}/api/legal-documents/terms`)
      .then(res => res.json())
      .then(data => {
        if (data.content && data.is_published !== false) {
          setContent(data.content);
          setVersion(data.version || '');
        } else {
          setContent(FALLBACK_CONTENT);
        }
      })
      .catch(() => setContent(FALLBACK_CONTENT))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/trial-signup" className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
            <ArrowLeft className="w-4 h-4" />
            Back to Sign Up
          </Link>
          <div className="flex items-center gap-2">
            <span className="font-bold text-stone-800">{appSettings.app_name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-stone-800 mb-2">Terms of Service</h1>
        <p className="text-stone-500 mb-8">{version ? `Version ${version}` : 'Current version'}</p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-600 mr-2" />
            <span className="text-stone-500">Loading...</span>
          </div>
        ) : (
          <div className="prose prose-stone max-w-none">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default TermsOfService;
